import { FeatureTypes } from '@/components/Billing/billing-types';

export enum AccountRoles {
	Admin = 'admin',
	Manager = 'manager',
	Creator = 'creator',
	LimitedCreator = 'limited_creator',
	PremiumEndUser = 'premium_end_user',
	StandardEndUser = 'standard_end_user',
	LiteEndUser = 'lite_end_user',
}

export const ADMIN_ROLE = AccountRoles.Admin;
export const MANAGER_ROLE = AccountRoles.Manager;
export const CREATOR_ROLE = AccountRoles.Creator;
export const LIMITED_CREATOR_ROLE = AccountRoles.LimitedCreator;
export const PREMIUM_END_USER_ROLE = AccountRoles.PremiumEndUser;
export const STANDARD_END_USER_ROLE = AccountRoles.StandardEndUser;
export const LITE_END_USER_ROLE = AccountRoles.LiteEndUser;

export const ACCOUNT_ROLES_HIERARCHY = [
	AccountRoles.Admin,
	AccountRoles.Manager,
	AccountRoles.Creator,
	AccountRoles.LimitedCreator,
	AccountRoles.PremiumEndUser,
	AccountRoles.StandardEndUser,
	AccountRoles.LiteEndUser,
];

export const ACCOUNT_ROLES: Record<AccountRoles, string> = {
	[AccountRoles.Admin]: 'Admin',
	[AccountRoles.Manager]: 'Manager',
	[AccountRoles.Creator]: 'Creator',
	[AccountRoles.LimitedCreator]: 'Limited creator',
	[AccountRoles.PremiumEndUser]: 'End-user (Premium member)',
	[AccountRoles.StandardEndUser]: 'End-user (Standard member)',
	[AccountRoles.LiteEndUser]: 'End-user (Lite member)',
};
export const ACCOUNT_ROLES_DESCRIPTIONS: Record<AccountRoles, string> = {
	[AccountRoles.Admin]: 'Can see and manage everything in the workspace, including billing',
	[AccountRoles.Manager]: 'Can create features and spaces, and invite users',
	[AccountRoles.Creator]: 'Can create features and spaces',
	[AccountRoles.LimitedCreator]: 'Can create features in existing spaces',
	[AccountRoles.PremiumEndUser]: 'Can use features in premium apps',
	[AccountRoles.StandardEndUser]: 'Can use features in standard apps',
	[AccountRoles.LiteEndUser]: 'Can use workspace-wide features in a URL or the home menu',
};

export const ACCOUNT_ROLES_BILLING_FEATURE: Record<AccountRoles, FeatureTypes | undefined> = {
	[AccountRoles.Admin]: undefined,
	[AccountRoles.Manager]: FeatureTypes.UserHierarchy,
	[AccountRoles.Creator]: FeatureTypes.UserHierarchy,
	[AccountRoles.LimitedCreator]: FeatureTypes.UserHierarchy,
	[AccountRoles.PremiumEndUser]: undefined,
	[AccountRoles.StandardEndUser]: undefined,
	[AccountRoles.LiteEndUser]: FeatureTypes.LiteUsersCount,
};

export const getOption = (value: string, text: string, subtext = '', disabled = false) => ({
	value,
	text,
	subtext,
	disabled,
});

/* Descriptions */
const USE = 'use features';
const INSTALL = 'install installables';
const SEE_GROUPS = 'see groups details';
const SHARE_FEATURES = 'share features';
const CREATE_FEATURES = 'create features';
const COLLABORATE = 'collaborate users in spaces';
const CREATE_SPACES = 'create spaces';
const ENFORCE_VISIBILITY = 'enforce feature visibility flag';
const MANAGE_GROUPS = 'manage groups';
const INVITE_USERS = 'invite users';
const EDIT_ACCOUNT = 'edit workspace';
const MANAGE_BILLING = 'manage billing';
const PREMIUM_CAPABILITIES = 'premium user capabilities';
const STANDARD_CAPABILITIES = 'standard user capabilities';
const LITE_CAPABILITIES = 'lite user capabilities';

export const PERMISSIONS_DESCRIPTIONS_MAP = {
	[AccountRoles.Admin]: [
		USE,
		INSTALL,
		SEE_GROUPS,
		SHARE_FEATURES,
		CREATE_FEATURES,
		COLLABORATE,
		CREATE_SPACES,
		ENFORCE_VISIBILITY,
		MANAGE_GROUPS,
		INVITE_USERS,
		EDIT_ACCOUNT,
		MANAGE_BILLING,
		PREMIUM_CAPABILITIES,
	],
	[AccountRoles.Manager]: [
		USE,
		INSTALL,
		SEE_GROUPS,
		SHARE_FEATURES,
		CREATE_FEATURES,
		COLLABORATE,
		CREATE_SPACES,
		ENFORCE_VISIBILITY,
		MANAGE_GROUPS,
		INVITE_USERS,
		EDIT_ACCOUNT,
		PREMIUM_CAPABILITIES,
	],
	[AccountRoles.Creator]: [USE, INSTALL, SEE_GROUPS, SHARE_FEATURES, CREATE_FEATURES, COLLABORATE, CREATE_SPACES, PREMIUM_CAPABILITIES],
	[AccountRoles.LimitedCreator]: [USE, INSTALL, SEE_GROUPS, SHARE_FEATURES, CREATE_FEATURES, PREMIUM_CAPABILITIES],
	[AccountRoles.PremiumEndUser]: [USE, PREMIUM_CAPABILITIES],
	[AccountRoles.StandardEndUser]: [USE, STANDARD_CAPABILITIES],
	[AccountRoles.LiteEndUser]: [USE, LITE_CAPABILITIES],
};

export const ALL_PERMISSIONS = [
	USE,
	INSTALL,
	SEE_GROUPS,
	SHARE_FEATURES,
	CREATE_FEATURES,
	COLLABORATE,
	CREATE_SPACES,
	ENFORCE_VISIBILITY,
	MANAGE_GROUPS,
	INVITE_USERS,
	EDIT_ACCOUNT,
	MANAGE_BILLING,
];
